/**
 * The rows-side row edits that change a sheet's LOCAL model only — label,
 * height, parent, delete — and resolve with no server verdict. They were
 * `ModelRow.setLabel` / `setHeight` / `reparent` / `delete`, which read
 * like persisted verbs (`await row.delete()` looked like a successful
 * delete when nothing reached the server); they are off the public row.
 *
 * Who calls them: the SDK (`@tento-lona/sheets/internal-sdk`), the
 * declarative op runner and row envelopes in this package, and fixtures /
 * offline tools (`@tento-lona/sheets/internal-testing`). An application persists
 * through the SDK instead: `handle.rows.delete(row, actor)`,
 * `sdkRow.update({ label }, actor)`, `handle.rows.move(...)`.
 *
 * Each is async only because an edit of a templated alias's synthetic child
 * (or a drop INTO an attached alias) forks the owning alias first
 * (`Sheet.detachAlias`), which materializes its children into real rows and
 * re-keys them; a real row takes the synchronous fast path, landing before
 * the returned promise.
 */
import type { Option } from "../core/option.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelRow } from "./model-row.ts";
import { sheetOf, sheetStores } from "../sheet/sheet-stores.ts";

/**
 * Edits to a rows-side row that change the sheet's local model only (label, height, parent, delete)
 * with no server verdict, for SDK internals and fixtures.
 */
export namespace RowLocalEdits {
  /** Set the row's label in the local model. Emits `RowLegendChanged(rowId)`. */
  export async function setLabel(row: ModelRow, label: string): Promise<void> {
    const sheet = sheetOf(row);
    const fork = sheet.aliasToFork(row.id);
    if (fork == null) {
      sheetStores(sheet).rowMetadata.patch(row.id, { label });
      return;
    }
    const remap = await sheet.detachAlias(fork);
    sheetStores(sheet).rowMetadata.patch(remap.get(row.id) ?? row.id, { label });
  }

  /** Set the row's height in pixels in the local model. Emits
   *  `RowHeightChanged(rowId)`. */
  export async function setHeight(
    row: ModelRow,
    heightPx: number,
  ): Promise<void> {
    const sheet = sheetOf(row);
    const fork = sheet.aliasToFork(row.id);
    if (fork == null) {
      sheetStores(sheet).rowMetadata.patch(row.id, { heightPx });
      return;
    }
    const remap = await sheet.detachAlias(fork);
    sheetStores(sheet).rowMetadata.patch(remap.get(row.id) ?? row.id, {
      heightPx,
    });
  }

  /**
   * Move the row under `newParent` (`null`: the sheet root) at `position`
   * in the local model. Emits a single `TreeMutation(op="reparent")`. The
   * moved row and the destination are resolved through an alias fork's
   * remap before the reparent lands (`Sheet.forkOwningAlias`).
   */
  export async function reparent(
    row: ModelRow,
    newParent: Option<ModelRow>,
    position: Option<number>,
  ): Promise<void> {
    const sheet = sheetOf(row);
    const selfFork = sheet.aliasToFork(row.id);
    // The destination fork is only ever resolved FOR a destination parent, so
    // carry the two together: a fork implies the parent id it forks.
    const dest = newParent != null
      ? { id: newParent.id, fork: sheet.aliasToFork(newParent.id) }
      : null;
    if (selfFork == null && dest?.fork == null) {
      sheetStores(sheet).tree.reparent(row.id, newParent?.id ?? null, position);
      return;
    }
    const remap = selfFork != null
      ? await sheet.detachAlias(selfFork)
      : new Map<RowLocalId, RowLocalId>();
    const movedId = remap.get(row.id) ?? row.id;
    let parentId: Option<RowLocalId> = newParent?.id ?? null;
    if (parentId !== null && remap.has(parentId)) {
      parentId = remap.get(parentId) ?? parentId;
    } else if (dest != null && dest.fork != null) {
      // `parentId` is still `dest.id` here (the branch above did not remap it).
      const destRemap = await sheet.detachAlias(dest.fork);
      parentId = destRemap.get(dest.id) ?? dest.id;
    }
    sheetStores(sheet).tree.reparent(movedId, parentId, position);
  }

  /**
   * Delete the row and every descendant from the local model. Emits one
   * `SheetStructureChanged` followed by `ColumnDataChanged` for each source
   * cell the deleted subtree owned; downstream formula rows recompute
   * (`Sheet.deleteRow`).
   */
  export async function remove(row: ModelRow): Promise<void> {
    const sheet = sheetOf(row);
    const fork = sheet.aliasToFork(row.id);
    if (fork == null) {
      sheet.deleteRow(row.id);
      return;
    }
    const remap = await sheet.detachAlias(fork);
    sheet.deleteRow(remap.get(row.id) ?? row.id);
  }
}
